import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { useAuth } from '@/hooks/useAuth'
import { descargarPdf } from '@/lib/documentos'
import { supabase } from '@/lib/supabase'
import { showToast } from '@/components/layout/Toast'
import type { Factura, Empresa, Cliente } from '@/lib/types'
import { formatCurrency, formatDate, r2 } from '@/lib/utils'
import {
  IconEdit, IconPaperclip, IconFile, IconImage,
  IconSend, IconCreditCard, IconCheckCircle,
} from '@/components/icons/Icons'
import SUNATPanel from '@/components/facturacion/SUNATPanel'
import { PageHeader, Breadcrumbs } from '@/components/ui'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

const estadoLabel: Record<string, string> = {
  pre_factura: 'Borrador',
  borrador: 'Borrador',
  emitida: 'Emitida',
  pago_reportado: 'Pago Reportado',
  pagada: 'Pagada',
  anulada: 'Anulada',
}

const estadoBadge: Record<string, string> = {
  pre_factura: 'badge-borrador',
  borrador: 'badge-borrador',
  emitida: 'badge-enviada',
  pago_reportado: 'badge-warning',
  pagada: 'badge-aceptada',
  anulada: 'badge-rechazada',
}

const nextStates: Record<string, { label: string; value: string; color: string }[]> = {
  pre_factura: [
    { label: 'Marcar Facturada', value: 'emitida', color: 'btn-primary' },
    { label: 'Anular', value: 'anulada', color: 'btn-danger' },
  ],
  borrador: [
    { label: 'Emitir', value: 'emitida', color: 'btn-primary' },
    { label: 'Anular', value: 'anulada', color: 'btn-danger' },
  ],
  emitida: [
    { label: 'Marcar Pagada', value: 'pagada', color: 'btn-primary' },
    { label: 'Anular', value: 'anulada', color: 'btn-danger' },
  ],
  pago_reportado: [
    { label: 'Confirmar Pago', value: 'pagada', color: 'btn-success' },
    { label: 'Rechazar Reporte', value: 'emitida', color: 'btn-secondary' },
  ],
  pagada: [
    { label: 'Anular', value: 'anulada', color: 'btn-danger' },
  ],
  anulada: [],
}

const PAYMENT_STATUS_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  none: { label: 'Sin enviar', color: '#95a5a6', bg: 'rgba(149,165,166,0.12)' },
  pending: { label: 'Enviado', color: '#f39c12', bg: 'rgba(243,156,18,0.12)' },
  paid: { label: 'Pagado', color: '#2ecc71', bg: 'rgba(46,204,113,0.12)' },
  rejected: { label: 'Rechazado', color: '#e94560', bg: 'rgba(233,69,96,0.12)' },
  cancelled: { label: 'Cancelado', color: '#95a5a6', bg: 'rgba(149,165,166,0.12)' },
}

export default function VerFactura() {
  const db = useSupabase()
  const { token } = useAuth()
  const nav = useNavigate()
  const { id } = useParams()
  const previewRef = useRef<HTMLDivElement>(null)

  const [factura, setFactura] = useState<Factura | null>(null)
  const [empresa, setEmpresa] = useState<Empresa | null>(null)

  const [cliente, setCliente] = useState<Cliente | null>(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [sendingPayment, setSendingPayment] = useState(false)
  const [sendingNotification, setSendingNotification] = useState(false)
  const [showDepositModal, setShowDepositModal] = useState(false)
  const [depositForm, setDepositForm] = useState({
    monto: '',
    fecha_deposito: new Date().toISOString().split('T')[0],
    numero_operacion: '',
    banco: '',
  })
  const [confirmingDeposit, setConfirmingDeposit] = useState(false)

  useEffect(() => {
    async function load() {
      try {
        const [f, empresas] = await Promise.all([
          db.getById<Factura>('facturas', Number(id)),
          db.getAll<Empresa>('empresa'),
        ])
        setFactura(f)
        if (empresas.length > 0) setEmpresa(empresas[0])

        // Load client company data for fiscal info
        if (f?.cliente_id) {
          const clienteData = await db.getById<Cliente>('clientes', f.cliente_id)
          if (clienteData) setCliente(clienteData)
        }
      } catch {
        showToast('Error al cargar factura', 'error')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id])

  // Timeline events
  interface TimelineEvent {
    id: number
    tipo: string
    canal: string
    asunto: string | null
    destinatario: string | null
    estado: string
    created_at: string
  }
  const [timeline, setTimeline] = useState<TimelineEvent[]>([])

  useEffect(() => {
    if (!factura) return
    supabase
      .from('notificacion_log')
      .select('id, tipo, canal, asunto, destinatario, estado, created_at')
      .or(`metadata->>factura_id.eq.${factura.id},asunto.ilike.%${factura.numero}%`)
      .order('created_at', { ascending: false })
      .limit(20)
      .then(({ data }) => {
        if (data) setTimeline(data as TimelineEvent[])
      })
  }, [factura?.id, factura?.estado])

  async function changeEstado(newEstado: string) {
    if (!factura) return
    try {
      await db.update('facturas', { id: factura.id, estado: newEstado })
      setFactura({ ...factura, estado: newEstado as Factura['estado'] })
      showToast(`Estado cambiado a ${estadoLabel[newEstado]}`, 'success')

      // Auto-notify client when payment is confirmed
      if (newEstado === 'pagada') {
        const { data: session } = await supabase.auth.getSession()
        const token = session?.session?.access_token
        if (token) {
          fetch(`${API_URL}/api/notifications/payment-confirmed`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ factura_id: factura.id }),
          }).catch(() => {})
        }
      }
    } catch {
      showToast('Error al cambiar estado', 'error')
    }
  }

  /*
    Baja el documento que arma el backend: el PDF de SUNAT si la factura está
    emitida, y si no el detalle de cobro de la plantilla `templates/factura`.

    Antes fotografiaba la pantalla con html2canvas y pegaba las imágenes en
    un jsPDF — una factura como foto, que el contador no puede copiar y que
    a veces salía con bloques vacíos. Ver `src/lib/documentos.ts`.
  */
  async function exportPDF() {
    if (!factura) return
    setExporting(true)
    try {
      await descargarPdf(
        `${API_URL}/api/pdf/factura/${factura.id}/archivo`,
        `Factura-${factura.numero}-${factura.proyecto_nombre || 'doc'}`,
        token,
      )
      showToast('PDF descargado', 'success')
    } catch {
      showToast('No se pudo descargar el documento', 'error')
    } finally {
      setExporting(false)
    }
  }

  // Send MP payment link via edge function
  async function handleSendPayment() {
    if (!factura) return
    setSendingPayment(true)
    try {
      const { data: session } = await supabase.auth.getSession()
      const token = session?.session?.access_token
      if (!token) throw new Error('No autenticado')

      const res = await fetch(
        `${API_URL}/api/facturas/${factura.id}/mp-send-payment-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      )
      const result = await res.json()
      if (!res.ok) throw new Error(result.error || 'Error enviando link de pago')

      showToast(`Link de pago enviado a ${result.sent_to?.join(', ')}`, 'success')

      // Refresh factura data
      const updated = await db.getById<Factura>('facturas', factura.id)
      if (updated) setFactura(updated)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error enviando link de pago'
      showToast(message, 'error')
    } finally {
      setSendingPayment(false)
    }
  }

  // Send invoice notification via edge function
  async function handleSendNotification() {
    if (!factura) return
    setSendingNotification(true)
    try {
      const { data: session } = await supabase.auth.getSession()
      const token = session?.session?.access_token
      if (!token) throw new Error('No autenticado')

      const res = await fetch(
        `${API_URL}/api/notifications/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ evento: 'factura_emitida', variables: { numero: factura.numero } }),
        }
      )
      const result = await res.json()
      if (!res.ok) throw new Error(result.error || 'Error enviando notificación')

      showToast('Notificación enviada al cliente', 'success')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error enviando notificación'
      showToast(message, 'error')
    } finally {
      setSendingNotification(false)
    }
  }

  // Confirm bank deposit manually
  async function handleConfirmDeposit() {
    if (!factura) return
    const monto = parseFloat(depositForm.monto)
    if (isNaN(monto) || monto <= 0) {
      showToast('Ingresa un monto valido', 'error')
      return
    }
    if (!depositForm.numero_operacion.trim()) {
      showToast('Ingresa el numero de operacion', 'error')
      return
    }

    setConfirmingDeposit(true)
    try {
      // El movimiento de caja se crea PRIMERO (con el detalle bancario). Al marcar
      // luego la factura 'pagada', el backend ve que ya existe un ingreso ligado a
      // esta factura y no lo duplica (idempotencia por factura_id).
      await db.add('movimientos_caja', {
        tipo: 'ingreso',
        categoria: 'Proyectos y Consultoría',
        descripcion: `Deposito bancario${depositForm.banco ? ` (${depositForm.banco})` : ''} - Factura ${factura.numero} - Op. ${depositForm.numero_operacion}`,
        monto: r2(monto),
        moneda: factura.moneda || 'USD',
        fecha: depositForm.fecha_deposito,
        factura_id: factura.id,
        cliente_id: factura.cliente_id,
        comprobantes: [],
        notas: `Banco: ${depositForm.banco || 'No especificado'}\nN° Operacion: ${depositForm.numero_operacion}`,
      })

      // Update factura to paid
      await db.update('facturas', {
        id: factura.id,
        estado: 'pagada',
        flow_status: 'paid',
        flow_payment_id: `DEP-${depositForm.numero_operacion}`,
        fecha_pago: new Date(depositForm.fecha_deposito).toISOString(),
      })

      showToast('Deposito confirmado', 'success')
      setShowDepositModal(false)

      // Auto-notify client (UX-07: usa el endpoint real; antes /functions/v1 → 404)
      const { data: session2 } = await supabase.auth.getSession()
      if (session2?.session?.access_token) {
        fetch(`${API_URL}/api/notifications/payment-confirmed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session2.session.access_token}` },
          body: JSON.stringify({ factura_id: factura.id }),
        }).catch(() => {})
      }

      // Refresh factura data
      const updated = await db.getById<Factura>('facturas', factura.id)
      if (updated) setFactura(updated)
    } catch {
      showToast('Error al confirmar deposito', 'error')
    } finally {
      setConfirmingDeposit(false)
    }
  }

  // Open deposit modal with pre-filled amount
  function openDepositModal() {
    if (!factura) return
    setDepositForm({
      monto: String(factura.total),
      fecha_deposito: new Date().toISOString().split('T')[0],
      numero_operacion: '',
      banco: '',
    })
    setShowDepositModal(true)
  }

  if (loading) return (
    <div className="page-enter" style={{ padding: '2rem' }}>
      <PageHeader breadcrumbs={[{ label: 'Facturas', to: '/facturas' }, { label: 'Factura' }]} />
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  if (!factura) return (
    <div className="page-enter" style={{ padding: '2rem' }}>
      <div className="empty-state">
        <h3>Factura no encontrada</h3>
        <button className="btn btn-primary" onClick={() => nav('/facturas')}>Volver</button>
      </div>
    </div>
  )

  const itemsFijos = factura.items.filter(i => i.tipo === 'fijo')
  const itemsVariables = factura.items.filter(i => i.tipo === 'variable')
  const paymentSt = PAYMENT_STATUS_BADGE[factura.flow_status || 'none'] || PAYMENT_STATUS_BADGE.none

  return (
    <div className="page-enter">
      {/* ─── Action bar ─── */}
      <div className="page-header page-header--crumbs">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div>
            <Breadcrumbs items={[
              { label: 'Facturas', to: '/facturas' },
              { label: factura.numero },
            ]} />
            {factura.nombre_interno && (
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: 2 }}>{factura.nombre_interno}</div>
            )}
          </div>
          <span className={`badge ${estadoBadge[factura.estado]}`}>{estadoLabel[factura.estado]}</span>
          {factura.sunat_estado && factura.sunat_estado !== 'no_emitida' && (
            <span className={`sunat-badge sunat-badge--${factura.sunat_estado === 'emitida' ? 'emitida' : factura.sunat_estado === 'error' ? 'error' : 'pending'}`}>
              {factura.sunat_estado === 'emitida' ? 'SUNAT' : factura.sunat_estado === 'error' ? 'Error SUNAT' : factura.sunat_estado === 'anulada' ? 'Anulada SUNAT' : 'Emitiendo'}
            </span>
          )}
          {factura.detraccion_aplica && (
            <span className="badge" style={{ background: 'rgba(233, 69, 96, 0.15)', color: '#e94560', fontWeight: 700 }}>SPOT</span>
          )}
          {/* Payment status badge */}
          {factura.flow_status && factura.flow_status !== 'none' && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 20, fontSize: '0.75rem',
              fontWeight: 600, background: paymentSt.bg, color: paymentSt.color,
            }}>
              <IconCreditCard size={12} /> {paymentSt.label}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {nextStates[factura.estado]?.map(action => (
            <button
              key={action.value}
              className={`btn btn-sm ${action.color}`}
              onClick={() => changeEstado(action.value)}
            >
              {action.label}
            </button>
          ))}
          {/* Payment actions — solo para facturas ya emitidas (no en borrador). UX-06 */}
          {factura.estado !== 'anulada' && factura.estado !== 'pagada' && factura.estado !== 'borrador' && factura.estado !== 'pre_factura' && factura.flow_status !== 'paid' && (
            <>
              <button
                className="btn btn-sm"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: '0.8rem',
                  background: factura.flow_status === 'pending' ? 'rgba(243,156,18,0.12)' : 'rgba(5,150,105,0.12)',
                  color: factura.flow_status === 'pending' ? '#f39c12' : '#059669',
                  border: 'none',
                }}
                onClick={handleSendPayment}
                disabled={sendingPayment}
                title={factura.flow_status === 'pending' ? 'Reenviar link de pago' : 'Enviar link de pago via Mercado Pago'}
              >
                <IconSend size={12} />
                {sendingPayment ? 'Enviando...' : factura.flow_status === 'pending' ? 'Reenviar Link' : 'Enviar Link de Pago'}
              </button>
              {factura.flow_url && (
                <button
                  className="btn btn-sm btn-secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}
                  onClick={() => {
                    navigator.clipboard.writeText(factura.flow_url!)
                      .then(() => showToast('Link de pago copiado al portapapeles', 'success'))
                      .catch(() => showToast('Error al copiar', 'error'))
                  }}
                  title="Copiar link de pago al portapapeles"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                  </svg>
                  Copiar Link
                </button>
              )}
              <button
                className="btn btn-sm btn-secondary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}
                onClick={openDepositModal}
              >
                <IconCheckCircle size={12} /> Confirmar Deposito
              </button>
            </>
          )}
          <button className="btn btn-sm btn-secondary" onClick={() => nav(`/facturas/${factura.id}/editar`)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconEdit size={14} /> Editar</button>
          <button className="btn btn-sm btn-primary" onClick={exportPDF} disabled={exporting}>
            {exporting ? 'Exportando...' : 'PDF'}
          </button>
          {factura.estado === 'emitida' && factura.cliente_email && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={handleSendNotification}
              disabled={sendingNotification}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              title="Reenviar notificación al cliente por email"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,7 12,13 2,7"/>
              </svg>
              {sendingNotification ? 'Enviando...' : 'Reenviar Notificación'}
            </button>
          )}
        </div>
      </div>

      {/* ─── Borrador workflow banner ─── */}
      {factura.estado === 'borrador' && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(79,127,232,0.10), rgba(52,44,222,0.08))',
          border: '1px solid rgba(79,127,232,0.25)',
          borderRadius: 12, padding: '16px 20px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4F7FE8" strokeWidth="2">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
              Borrador — Pendiente de aprobación
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
              Descarga el PDF para revisión. Cuando sea aprobado, haz clic en <strong>"Emitir"</strong> para marcarla como emitida.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-sm btn-primary"
              onClick={exportPDF}
              disabled={exporting}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              {exporting ? 'Exportando...' : 'Descargar PDF'}
            </button>
            <button
              className="btn btn-sm"
              style={{ background: 'rgba(46,204,113,0.15)', color: '#2ecc71', border: '1px solid rgba(46,204,113,0.3)', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              onClick={() => changeEstado('emitida')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/>
              </svg>
              Emitir
            </button>
          </div>
        </div>
      )}

      {/* ─── SUNAT Panel ─── */}
      <SUNATPanel factura={factura} onUpdate={setFactura} />

      {/* ─── Notification Timeline ─── */}
      {timeline.length > 0 && (
        <div style={{
          background: 'var(--card-bg)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '20px 24px', marginBottom: 20,
        }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            Historial de Notificaciones
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {timeline.map((evt, idx) => (
              <div key={evt.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '10px 0',
                borderBottom: idx < timeline.length - 1 ? '1px solid var(--border)' : 'none',
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem',
                  background: evt.estado === 'sent' || evt.estado === 'enviado'
                    ? 'rgba(46,204,113,0.15)' : 'rgba(233,69,96,0.15)',
                  color: evt.estado === 'sent' || evt.estado === 'enviado'
                    ? '#2ecc71' : '#e94560',
                }}>
                  {evt.canal === 'whatsapp' ? '📱' : evt.canal === 'resend' || evt.canal === 'email' ? '📧' : '🔔'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {evt.asunto || evt.tipo || 'Notificación'}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                    {evt.destinatario && <span>→ {evt.destinatario} · </span>}
                    {new Date(evt.created_at).toLocaleDateString('es-PE', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                    })}
                  </div>
                </div>
                <span style={{
                  fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                  background: evt.estado === 'sent' || evt.estado === 'enviado'
                    ? 'rgba(46,204,113,0.12)' : 'rgba(233,69,96,0.12)',
                  color: evt.estado === 'sent' || evt.estado === 'enviado'
                    ? '#2ecc71' : '#e94560',
                }}>
                  {evt.estado === 'sent' || evt.estado === 'enviado' ? 'Enviado' : 'Error'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Document preview ─── */}
      <div ref={previewRef} className="cotizacion-preview" style={{
        background: '#fff', color: '#1a1a2e', padding: '40px', borderRadius: 12,
        maxWidth: 800, margin: '0 auto',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 30, borderBottom: '3px solid #1C4587', paddingBottom: 20 }}>
          <div>
            <img src="/assets/brand/logo-full-color.svg" alt="1Conecta" style={{ height: 36, marginBottom: 10 }} crossOrigin="anonymous" />
            <div style={{ fontSize: 12, color: '#666' }}>{empresa?.razon_social}</div>
            {empresa?.ruc && <div style={{ fontSize: 12, color: '#666' }}>RUC: {empresa.ruc}</div>}
            {empresa?.ubicacion && <div style={{ fontSize: 11, color: '#888' }}>{empresa.ubicacion}</div>}
            {empresa?.telefono && <div style={{ fontSize: 11, color: '#888' }}>Tel: {empresa.telefono}</div>}
            {empresa?.email && <div style={{ fontSize: 11, color: '#888' }}>{empresa.email}</div>}
            {empresa?.website && <div style={{ fontSize: 11, color: '#888' }}>{empresa.website}</div>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 700, fontSize: 22, color: '#1C4587' }}>
              {factura.estado === 'borrador' ? 'PRE-FACTURA' : 'FACTURA'}
            </div>
            <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'monospace' }}>N° {factura.numero}</div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              Fecha: {formatDate(factura.fecha_emision)}
            </div>
            {factura.fecha_vencimiento && (
              <div style={{ fontSize: 12, color: '#666' }}>
                Vencimiento: {formatDate(factura.fecha_vencimiento)}
              </div>
            )}
            {factura.moneda !== 'PEN' && factura.tipo_cambio && (
              <div style={{ fontSize: 12, color: '#666' }}>
                T.C. Venta: {factura.tipo_cambio.toFixed(3)}
              </div>
            )}
          </div>
        </div>

        {/* Client info */}
        <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, marginBottom: 24 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, color: '#1C4587' }}>CLIENTE:</div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{cliente?.razon_social || factura.cliente_nombre}</div>
          {(cliente?.ruc_dni || factura.cliente_ruc) && <div style={{ fontSize: 13 }}>RUC: {cliente?.ruc_dni || factura.cliente_ruc}</div>}
          {cliente?.direccion_fiscal && <div style={{ fontSize: 13, color: '#555' }}>{cliente.direccion_fiscal}</div>}
          {cliente?.ubigeo && <div style={{ fontSize: 12, color: '#888' }}>Ubigeo: {cliente.ubigeo}</div>}
          {factura.proyecto_nombre && <div style={{ fontSize: 13, marginTop: 8, fontStyle: 'italic', color: '#555', borderTop: '1px solid #e0e0e0', paddingTop: 6 }}>Proyecto: {factura.proyecto_nombre}</div>}
        </div>

        {/* Items table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
          <thead>
            <tr style={{ background: '#1C4587', color: '#fff' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13 }}>Tipo</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13 }}>Descripcion</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, whiteSpace: 'nowrap' }}>P. Unit.</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 13, whiteSpace: 'nowrap' }}>Cant.</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, whiteSpace: 'nowrap' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {factura.items.map((item, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                <td style={{ padding: '8px 12px', fontSize: 13 }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                    background: item.tipo === 'fijo' ? '#e3f2fd' : '#fff3e0',
                    color: item.tipo === 'fijo' ? '#1565c0' : '#e65100',
                  }}>
                    {item.tipo === 'fijo' ? 'Fijo' : 'Variable'}
                  </span>
                </td>
                <td style={{ padding: '8px 12px', fontSize: 13 }}>{item.descripcion}</td>
                <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'right', whiteSpace: 'nowrap' }}>{formatCurrency(item.precio_unitario, factura.moneda)}</td>
                <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'center', whiteSpace: 'nowrap' }}>{item.cantidad}</td>
                <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{formatCurrency(item.total, factura.moneda)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Summary breakdown by type */}
        {itemsFijos.length > 0 && itemsVariables.length > 0 && (
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <div style={{ flex: 1, padding: 12, background: '#e3f2fd', borderRadius: 8, fontSize: 13 }}>
              <strong>Cargos Fijos:</strong> {formatCurrency(r2(itemsFijos.reduce((s, i) => s + i.total, 0)), factura.moneda)}
            </div>
            <div style={{ flex: 1, padding: 12, background: '#fff3e0', borderRadius: 8, fontSize: 13 }}>
              <strong>Excedentes Variables:</strong> {formatCurrency(r2(itemsVariables.reduce((s, i) => s + i.total, 0)), factura.moneda)}
            </div>
          </div>
        )}

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
          <div style={{ minWidth: 300 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e0e0e0' }}>
              <span style={{ color: '#666' }}>Subtotal</span>
              <span style={{ fontWeight: 600 }}>{formatCurrency(factura.subtotal, factura.moneda)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e0e0e0' }}>
              <span style={{ color: '#666' }}>IGV ({factura.igv_porcentaje}%)</span>
              <span style={{ fontWeight: 600 }}>{formatCurrency(factura.igv_monto, factura.moneda)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontWeight: 700, fontSize: '1.2rem', borderTop: '2px solid #1C4587', marginTop: 4 }}>
              <span>TOTAL</span>
              <span style={{ color: '#1C4587' }}>{formatCurrency(factura.total, factura.moneda)}</span>
            </div>
            {/* Total en PEN si moneda extranjera */}
            {factura.moneda !== 'PEN' && factura.tipo_cambio && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, color: '#666' }}>
                <span>Total PEN (TC {factura.tipo_cambio.toFixed(3)})</span>
                <span>S/ {(factura.total_pen ?? r2(factura.total * (factura.tipo_cambio ?? 1))).toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
          </div>
        </div>



        {/* Bank details */}
        {empresa && (empresa.banco || empresa.cuenta_corriente) && (
          <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, marginBottom: 24 }}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#1C4587' }}>DATOS BANCARIOS</div>
            <table style={{ fontSize: 13 }}>
              <tbody>
                {empresa.banco && <tr><td style={{ padding: '2px 12px 2px 0', fontWeight: 600 }}>Banco:</td><td>{empresa.banco}</td></tr>}
                {empresa.cuenta_corriente && <tr><td style={{ padding: '2px 12px 2px 0', fontWeight: 600 }}>Cuenta Corriente:</td><td style={{ fontFamily: 'monospace' }}>{empresa.cuenta_corriente}</td></tr>}
                {empresa.cci && <tr><td style={{ padding: '2px 12px 2px 0', fontWeight: 600 }}>CCI:</td><td style={{ fontFamily: 'monospace' }}>{empresa.cci}</td></tr>}
                {empresa.swift && <tr><td style={{ padding: '2px 12px 2px 0', fontWeight: 600 }}>SWIFT:</td><td style={{ fontFamily: 'monospace' }}>{empresa.swift}</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes */}
        {factura.notas && (
          <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, marginBottom: 24 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#1C4587' }}>NOTAS</div>
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{factura.notas}</div>
          </div>
        )}

        {/* Adjuntos */}
        {factura.adjuntos && factura.adjuntos.length > 0 && (
          <div style={{ borderTop: '1px dashed #ccc', paddingTop: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#1C4587', display: 'flex', alignItems: 'center', gap: 6 }}><IconPaperclip size={14} /> SUSTENTOS ADJUNTOS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {factura.adjuntos.map((adj, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <span>{adj.tipo.includes('pdf') ? <IconFile size={14} /> : <IconImage size={14} />}</span>
                  <a href={adj.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1C4587' }}>{adj.nombre}</a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ─── Deposit Confirmation Modal ─── */}
      {showDepositModal && (
        <div className="modal-overlay" onClick={() => setShowDepositModal(false)}>
          <div className="modal deposit-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirmar Deposito Bancario</h3>
              <button className="modal-close" onClick={() => setShowDepositModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Monto depositado ({factura.moneda})</label>
                <input
                  type="number"
                  className="form-input"
                  value={depositForm.monto}
                  onChange={e => setDepositForm(prev => ({ ...prev, monto: e.target.value }))}
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Fecha del deposito</label>
                  <input
                    type="date"
                    className="form-input"
                    value={depositForm.fecha_deposito}
                    onChange={e => setDepositForm(prev => ({ ...prev, fecha_deposito: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Banco</label>
                  <input
                    type="text"
                    className="form-input"
                    value={depositForm.banco}
                    placeholder="Ej: BCP, Interbank..."
                    onChange={e => setDepositForm(prev => ({ ...prev, banco: e.target.value }))}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Numero de operacion</label>
                <input
                  type="text"
                  className="form-input"
                  value={depositForm.numero_operacion}
                  placeholder="Ej: 00123456789"
                  onChange={e => setDepositForm(prev => ({ ...prev, numero_operacion: e.target.value }))}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowDepositModal(false)}>Cancelar</button>
              <button
                className="btn btn-success"
                onClick={handleConfirmDeposit}
                disabled={confirmingDeposit}
              >
                {confirmingDeposit ? 'Confirmando...' : 'Confirmar Deposito'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
